import {useEffect, useId, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCancel} from "@fortawesome/free-solid-svg-icons/faCancel";
import {faCheckCircle} from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import {faCircle} from "@fortawesome/free-regular-svg-icons/faCircle";
import "../EditCardRibbon.css";
import SelectedChild from "../SelectedChild";
import EditableEntry from "./EditableEntry";

const EditableAutoCompleteBase = ({
                                      isEditing = true,
                                      label = "",
                                      displayValue = "",
                                      items,
                                      onSelect,
                                      inputID = null,
                                      onCancel = () => {},
                                      placeholder,
                                      defaultValue,
                                      idKey = "id",
                                      nameKey = "name",
                                      cancelButton = false,
                                      requestSubmission = () => {},
                                      multiSelect = false,
    onKeyDown = () => {},
                                      onPaste = () => {},
    width
                                  }) => {
    const [filterContent, setFilterContent] = useState(defaultValue ?? "");
    const [focus, setFocus] = useState(false);
    const [selectedItems, setSelectedItems] = useState(new Set(defaultValue ? defaultValue : []));

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const backupID = useId();

    useEffect(() => {
        setSelectedItems(new Set(defaultValue ? defaultValue : []));
    }, [defaultValue]);

    function filter(fc = filterContent) {
        return items.filter(item => (nameKey in item) && item[nameKey].toLowerCase().includes(fc.toString().toLowerCase()));
    }

    const handleClickOutside = e => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            if (focus && filter().length === 1) {
                toggleSelect((filter())[0]);
            }

            setFocus(false);
        }
    };

    const handleBlurring = e => {
        if (e.relatedTarget && !e.target.parentNode.contains(e.relatedTarget)) {
            if (focus && filter().length === 1) {
                toggleSelect((filter())[0]);
            }

            setFocus(false);
        }
    };

    useEffect(() => {
        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    });

    useEffect(() => {
        if (multiSelect) {
            setSelectedItems(new Set(defaultValue ? defaultValue : []));
        } else {
            if (inputRef.current)
            inputRef.current.value = defaultValue ?? "";
            setFilterContent(defaultValue ?? '');
        }
    }, [defaultValue, inputRef]);

    function toggleSelect(item) {
        const id = item[idKey];

        if (multiSelect) {
            if (inputRef.current)
            inputRef.current.value = '';
            if (selectedItems.has(id)) {
                selectedItems.delete(id);
            } else {
                selectedItems.add(id);
            }
            setSelectedItems(selectedItems);
            onSelect([...selectedItems]);
            setFilterContent('');
        } else {
            if (inputRef.current)
                inputRef.current.value = item[nameKey];
            if (selectedItems.has(id)) {
                selectedItems.delete(id);
                onSelect(0);
            } else {
                setSelectedItems(new Set([id]));
                onSelect(id);
            }
        }

        setFocus(false);
    }

    function keyListener(e) {
        console.log("KD", e.key);
        if (e.key === "Enter") {
            if (filter().length === 1) {
                const item = (filter())[0];
                toggleSelect(item);
                if (multiSelect) {
                    e.preventDefault();
                } else {
                    requestSubmission();
                }
            } else if (multiSelect && !focus) {
                requestSubmission();
            }
        } else if (!focus && e.key !== "Tab") {
            setFocus(true);
        } else {
            if (onKeyDown(e)) {
                e.preventDefault();
                requestSubmission();
            }
        }
    }

    return <EditableEntry
        label={label}
        id={inputID ?? backupID}
        isEditing={isEditing}
        editingChildren={<>
            <div className={"autocompleteParent noexpand"} ref={wrapperRef} onBlur={handleBlurring}>
                <div style={{display: "flex", alignItems: "center"}}>
                    <input
                        style={{marginBottom: 0, marginRight: cancelButton ? "-1.5rem" : '', width}}
                        id={inputID ?? inputID}
                        defaultValue={multiSelect ? "" : defaultValue ?? ""}
                        type={"text"}
                        placeholder={placeholder}
                        ref={inputRef}
                        onFocus={() => setFocus(true)}
                        onChange={(e) => {
                            console.log("Change", e.target.value);
                            if (!multiSelect && e.target.value === "") {
                                onSelect(-1);
                            }
                            setFilterContent(e.target.value);
                        }}
                        onPaste={(e) => {
                            let back = onPaste(e);
                            if (back) {
                                if (filter(back).length === 1) {
                                    toggleSelect((filter(back))[0]);
                                }
                            }
                        } }
                        onKeyDown={keyListener}
                    />
                    {cancelButton && !multiSelect ? (
                        <>
                            <FontAwesomeIcon className={"stateless-icon-button"} icon={faCancel} onClick={(e) => {
                                inputRef.current.value = "";
                                setFilterContent("");
                                onCancel();
                                e.stopPropagation();
                            }}/>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                <ul className={"autocompleteList"}>
                    {focus ? filter().map(item => (
                        <li
                            className={"lst-items"}
                            onClick={(e) => {
                                toggleSelect(item);
                                if (multiSelect) {
                                    inputRef.current.value = '';
                                    setFilterContent('');
                                    inputRef.current.focus();
                                    setFocus(false);
                                }
                            }}
                        >
                            {selectedItems.has(item[idKey]) ?
                                <FontAwesomeIcon icon={faCheckCircle}/> :
                                <FontAwesomeIcon icon={faCircle}/>
                            } {item[nameKey]}
                        </li>
                    )) : <></>}
                </ul>
            </div>
            {multiSelect ? (
                <div className={"multiselected-parent"}>
                    {[...selectedItems].map((item) => {
                        const selectedItem = items.find(it => it[idKey] === item);
                        return (
                            <SelectedChild onClick={() => toggleSelect(selectedItem)}>
                                {selectedItem ? selectedItem[nameKey] : item}
                            </SelectedChild>
                        );
                    })}
                </div>
            ) : null}
        </>}
        viewingChildren={displayValue ?? [...selectedItems].map((item) => {
            const selectedItem = items.find(it => it[idKey] === item);
            if (!selectedItem)
                return false
            return selectedItem[nameKey];
        }).filter(it => it !== false).join(", ")}
    />;
}

export default EditableAutoCompleteBase;